// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Dialog, Typography, RadioGroup, Radio, FormControl, Button, FormControlLabel, LinearProgress, DialogActions, MenuItem, Select, InputBase } from '@mui/material/';
import { Box, useTheme } from '@mui/system';
import SVG from 'react-inlinesvg';
import { useAppDispatch, useAppSelector } from 'Hooks';
import Dropzone from 'Components/dropzone';
import uploadKmlIcon from 'Assets/upload-kml-icon.svg';
import { FileUploadStartThunk } from 'Features/fileUpload/fileUploadThunk';
import { SetExpressAIUploadAction, SetDxfEpsgAction, HideUploadSingleFileDialogAction } from 'Features/fileUpload/fileUploadActions';
import stylesDef from './styles';

interface ComponentProps {
  fileExtension: any;
  adminOutputUpload: boolean;
  containerId?: string;
  fileType?: string;
  setGdbError: (error: boolean) => void;
  onUploadError?: (message: string) => void;
}

// The .DXF option asks START then EPSG before the DROPZONE; a dxf dropped under OTHER asks the same questions after the
// drop (OTHER_DXF_QUESTION, then EPSG) and uploads on OTHER_DXF_PROGRESS
const PAGE = { START: 0, EPSG: 1, DROPZONE: 2, OTHER_DXF_QUESTION: 3, OTHER_DXF_PROGRESS: 4 };

const SingleFileUploadForm: React.FC<ComponentProps> = ({ fileExtension, adminOutputUpload, containerId, fileType, setGdbError, onUploadError }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const uploadFiles = useAppSelector((state) => state.fileUpload.uploadFiles);
  const expressAIUpload = useAppSelector((state) => state.fileUpload.expressAIUpload);
  const epsg = useAppSelector((state) => state.fileUpload.epsg);
  const epsgList = useAppSelector((state) => state.fileUpload.epsgList);
  const showUploadSingleFileDialog = useAppSelector((state) => state.fileUpload.showUploadSingleFileDialog);
  const file = uploadFiles[0];

  const [page, setPage] = useState(PAGE.START);
  const [dxfFile, setDxfFile] = useState(null);

  useEffect(() => {
    dispatch(HideUploadSingleFileDialogAction());
    if (epsgList[0]) {
      dispatch(SetDxfEpsgAction(epsgList[0]));
    }
  }, []);

  // One upload and one error hand-off for every path; admin output uploads surface a server rejection in the order card
  const uploadAndClose = async (uploadFile: File) => {
    const result = await dispatch(FileUploadStartThunk([uploadFile], false, false, false, false, false, adminOutputUpload, fileType)) as { error?: string } | undefined;
    onClose();
    if (result?.error) onUploadError(result.error);
  };

  // The dxf under OTHER is already chosen when its questions end, so it uploads on the progress page
  const uploadOtherDxf = () => {
    setPage(PAGE.OTHER_DXF_PROGRESS);
    uploadAndClose(dxfFile);
  };

  const onClose = (e = {}, reason = '') => {
    // disable backdropClick when client upload kml, so that the dialog will close when kmlCheck lambda is finished
    if (!_ADMIN_ && reason === 'backdropClick' && (fileExtension.includes('.kml') || fileExtension.includes('.kmz')) && uploadFiles.length) {
      return;
    }
    setDxfFile(null);
    setPage(PAGE.START);
    dispatch(HideUploadSingleFileDialogAction());
  };

  const afterQuestions = () => (fileType === 'dxf' ? setPage(PAGE.DROPZONE) : uploadOtherDxf());

  const goToNextPage = () => {
    if (expressAIUpload) {
      afterQuestions();
    } else {
      setPage(PAGE.EPSG);
    }
  };

  const askExpressAI = adminOutputUpload && ((page === PAGE.START && fileType === 'dxf') || page === PAGE.OTHER_DXF_QUESTION);
  const askEpsg = adminOutputUpload && page === PAGE.EPSG;
  const isProgressPage = page === PAGE.OTHER_DXF_PROGRESS;

  return (
    <Dialog
      open={showUploadSingleFileDialog}
      onClose={onClose}
      sx={{
        '& .MuiDialog-root': styles.dialogRoot,
        '& .MuiDialog-paper': styles.dialog,
        '& .MuiDialog-paperFullScreen': styles.paperFullscreen,
      }}
      fullScreen={!containerId}
      container={() => document.getElementById(containerId)}
    >
      <Box sx={styles.dropzoneWrapper}>
        {askExpressAI && (
          <Box sx={styles.expressAIOptions}>
            <Typography variant="h5" sx={styles.dialogText}>Please indicate whether this file is Express AI:</Typography>
            <FormControl>
              <RadioGroup
                name="radio-buttons-group"
                value={expressAIUpload}
                onChange={() => dispatch(SetExpressAIUploadAction())}
              >
                <FormControlLabel sx={styles.radioButton} value control={<Radio />} label="Yes, it is Express AI" />
                <FormControlLabel sx={styles.radioButton} value={false} control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <DialogActions sx={styles.dialogActions}>
              <Button onClick={onClose} variant="text">Close</Button>
              <Button onClick={goToNextPage} variant="contained" color="primary">Continue</Button>
            </DialogActions>
          </Box>
        )}
        {askEpsg && (
          <Box sx={styles.expressAIOptions}>
            <Typography variant="h5" sx={styles.dialogText}>Select EPSG Code</Typography>
            <Typography variant="body1">
              Please select below the
              {' '}
              <span style={styles.bold}>
                EPSG
              </span>
              {' '}
              code for your file(s).
            </Typography>
            <FormControl>
              <Select
                variant="outlined"
                input={<InputBase />}
                sx={styles.dropdown}
                value={epsg}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  if (epsgList[idx]) {
                    dispatch(SetDxfEpsgAction(epsgList[idx]));
                  }
                }}
                renderValue={() => {
                  const entry = epsgList.find((item) => item.epsg === epsg);
                  if (!entry) return `EPSG: ${epsg}`;
                  return entry.verticalEpsg
                    ? `EPSG: ${entry.epsg} + ${entry.verticalEpsg}`
                    : `EPSG: ${entry.epsg}`;
                }}
              >
                {epsgList.map((item, idx) => (
                  <MenuItem key={`${item.epsg}-${item.verticalEpsg || ''}`} value={idx}>
                    EPSG:
                    {' '}
                    {item.epsg}
                    {item.verticalEpsg ? ` + ${item.verticalEpsg}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DialogActions sx={styles.dialogActions}>
              <Button onClick={onClose} variant="text">Close</Button>
              <Button onClick={afterQuestions} variant="contained" color="primary">Upload Now</Button>
            </DialogActions>
          </Box>
        )}
        {!askExpressAI && !askEpsg && (
          <>
            {!isProgressPage && (
              <Dropzone
                acceptedExtensions={fileExtension}
                onDropAccepted={async (acceptedFilesArray) => {
                  const file = acceptedFilesArray[0];
                  // The dropzone reports a refused file as an empty list
                  if (!file) return;
                  const fileExtension = file.name.split('.').pop()?.toLowerCase();

                  if (fileExtension === 'dxf' && fileType === 'other') {
                    setDxfFile(file);
                    setPage(PAGE.OTHER_DXF_QUESTION);
                  } else if (fileExtension === 'dxf' && expressAIUpload) {
                    setDxfFile(file);
                  } else {
                    if (fileType === 'gdb' && !file.name.endsWith('.zip') && !file.name.endsWith('.rar')) {
                      setGdbError(true);
                      return;
                    }
                    await uploadAndClose(file);
                  }
                }}
              >
                {(getInputProps, open) => (
                  <>
                    <Box sx={styles.uploadIconWrapper}>
                      <SVG src={uploadKmlIcon} title="Upload" css={styles.uploadFormIcon} />
                    </Box>
                    <br />
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <input {...getInputProps()} />
                    <Typography variant="h5" sx={styles.uploadFormText}>
                      Drag and drop to upload
                    </Typography>
                    <br />
                    <Typography variant="caption" sx={styles.uploadFormText}>
                      {'or '}
                      <button type="button" style={styles.openFileDialogLink} onClick={open}>browse</button>
                      {' to choose a file'}
                    </Typography>
                  </>
                )}
              </Dropzone>
            )}
            {file && (
              <Box sx={[styles.progressRow, isProgressPage && styles.progressRowCentered]}>
                <Box>
                  <Typography variant="body2">{file.name}</Typography>
                </Box>
                <Typography variant="body2">
                  {file.progress.toFixed(2)}
                  {' '}
                  %
                  {' '}
                </Typography>
                <Box sx={[styles.progress, isProgressPage && styles.progressWide]}>
                  <LinearProgress
                    color="primary"
                    variant="determinate"
                    value={file.progress}
                  />
                </Box>
              </Box>
            )}
            {(dxfFile && !file) && (
              <Box sx={[styles.dxfFile, isProgressPage && styles.dxfFileCentered]}>
                <Typography variant="body2">{dxfFile.name}</Typography>
              </Box>
            )}
            {expressAIUpload && adminOutputUpload && !isProgressPage && (
              <DialogActions sx={styles.dialogActions}>
                <Button onClick={onClose} variant="text">Close</Button>
                <Button onClick={() => uploadAndClose(dxfFile)} variant="contained" color="primary">Upload Now</Button>
              </DialogActions>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

// Customer dialogs pass no handler: their failures are shown by the upload flow itself (kml) or not at all today (dxf)
SingleFileUploadForm.defaultProps = {
  onUploadError: () => {},
};

export default SingleFileUploadForm;
